<template>
  <v-row justify="center">
    <v-dialog v-model="openDialog" persistent max-width="800px">
      <v-card>
        <v-card-title class="mx-3 pl-6 pt-4">
          <span v-if="localGroup._id" class="text-h5">
            Benutzergruppe bearbeiten
          </span>
          <span v-else class="text-h5">Neue Benutzergruppe erstellen</span>
        </v-card-title>
        <v-divider class="mx-9" />
        <v-card-text>
          <v-container>
            <v-form ref="form" v-model="valid">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    bg-color="accent"
                    variant="filled"
                    label="Gruppenname"
                    :rules="[rules.required, rules.max50]"
                    v-model="localGroup.name"
                  />
                </v-col>
              </v-row>

              <div class="mx-0">
                <span class="text-h6">Gruppentyp</span>
                <v-divider class="mb-5" />
              </div>
              <v-row class="mx-0 mt-4" style="display: flex; gap: 10px">
                <v-col
                  class="pa-0"
                  v-for="type in ['instanz', 'mandant']"
                  :key="type"
                  :style="{
                    cursor: isDisabled(type) ? 'not-allowed' : 'pointer',
                  }"
                >
                  <v-tooltip location="bottom" v-if="tooltipText(type)">
                    <template v-slot:activator="{ props }">
                      <div v-bind="props">
                        <v-btn
                          variant="outlined"
                          style="width: 100%; min-height: 120px"
                          :style="getTypeStyle(type)"
                          @click="selectType(type)"
                          :disabled="isDisabled(type)"
                        >
                          <div class="d-flex flex-column">
                            <span class="mb-2">
                              {{
                                type === "instanz"
                                  ? "Instanz-Ebene"
                                  : "Mandanten-Ebene"
                              }}
                            </span>
                            <span>
                              {{
                                type === "instanz"
                                  ? "Verfügbar für alle Mandanten"
                                  : `Spezifisch für ${tenant?.name}`
                              }}
                            </span>
                          </div>
                        </v-btn>
                      </div>
                    </template>
                    <span>{{ tooltipText(type) }}</span>
                  </v-tooltip>
                  <template v-else>
                    <v-btn
                      variant="outlined"
                      style="width: 100%; min-height: 120px"
                      :style="getTypeStyle(type)"
                      @click="selectType(type)"
                      :disabled="isDisabled(type)"
                    >
                      <div class="d-flex flex-column">
                        <span class="mb-2">
                          {{
                            type === "instanz"
                              ? "Instanz-Ebene"
                              : "Mandanten-Ebene"
                          }}
                        </span>
                        <span>
                          {{
                            type === "instanz"
                              ? "Verfügbar für alle Mandanten"
                              : `Spezifisch für ${tenant?.name}`
                          }}
                        </span>
                      </div>
                    </v-btn>
                  </template>
                </v-col>
              </v-row>

              <v-row
                class="mt-4"
                v-if="isInstanceOwner && localGroup.type === 'instanz'"
              >
                <v-col>
                  <v-card variant="outline" border class="pa-4 custom-border-radius">
                    <v-row class="align-center">
                      <v-col cols="auto">
                        <v-checkbox
                          v-model="localGroup.allowAssignmentToTenantUsers"
                          hide-details
                          class="ma-0 pa-0"
                        />
                      </v-col>
                      <v-col>
                        <span>
                          Erlaube dem Besitzer des Mandanten, diese
                          Instanzgruppe seinen Benutzern zuzuweisen. Wenn
                          deaktiviert, kann nur der Besitzer der Instanz
                          Zuweisungen verwalten.
                        </span>
                      </v-col>
                    </v-row>
                  </v-card>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="mb-5"
            color="primary"
            variant="flat"
            @click="submitChanges"
            :loading="inProgress"
          >
            Speichern
          </v-btn>
          <v-btn class="mb-5 mr-5" variant="outlined" @click="closeDialog">
            Abbrechen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { mapGetters } from "vuex";
import ApiUserGroupService from "../../services/api/ApiUserGroupService";

export default {
  name: "UserGroupEdit",
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    group: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      valid: true,
      inProgress: false,
      localGroup: {
        tenantId: this.isInstanceOwner ? null : this.tenantId,
        name: "",
        allowAssignmentToTenantUsers: false,
        type: "",
      },
      rules: {
        required: (value) => !!value || "Pflichtfeld",
        max50: (v) => !v || v.length <= 50 || "Maximal 50 Zeichen erlaubt",
      },
    };
  },
  computed: {
    ...mapGetters({
      tenantId: "tenants/currentTenantId",
      tenant: "tenants/currentTenant",
      isInstanceOwner: "user/isInstanceOwner",
    }),
    openDialog: {
      get() {
        return this.open;
      },
    },
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.resetLocalGroup();
        this.$refs.form?.resetValidation();
      }
    },
  },
  methods: {
    closeDialog() {
      this.$emit("close");
    },
    isDisabled(type) {
      return !!this.group?.id || (type === "instanz" && !this.isInstanceOwner);
    },
    tooltipText(type) {
      if (this.group?.id) {
        return "Gruppentyp kann nach Erstellung nicht mehr geändert werden";
      }
      if (type === "instanz" && !this.isInstanceOwner) {
        return "Nur Instanz-Besitzer können Instanzgruppen erstellen";
      }
      return null;
    },
    getTypeStyle(type) {
      const isDark = this.$vuetify.theme.global.name === "dark";
      const isActive = this.localGroup.type === type;
      const disabled = this.isDisabled(type);

      return {
        borderColor: isActive ? "#0099DB" : "#0000001f",
        backgroundColor: disabled
          ? isDark
            ? "#3a3a3a"
            : "#e0e0e0"
          : isDark
          ? "#1e1e1e"
          : "#ffffff",
        borderWidth: "2px",
      };
    },
    selectType(type) {
      this.localGroup.type = type;

      if (type === "instanz") {
        this.localGroup.tenantId = null;
      } else if (type === "mandant") {
        this.localGroup.tenantId = this.tenantId;
      }
    },
    resetLocalGroup() {
      this.localGroup = {
        tenantId: this.isInstanceOwner ? null : this.tenantId,
        name: "",
        allowAssignmentToTenantUsers: false,
        type: "",
        ...(this.group ?? {}),
      };

      if (this.localGroup.tenantId) {
        this.localGroup.type = "mandant";
      } else {
        this.localGroup.type = "instanz";
      }
    },
    async submitChanges() {
      this.inProgress = true;
      const result = await this.$refs.form?.validate();
      if (result.valid && this.localGroup.type) {
        try {
          await ApiUserGroupService.submitUserGroup(this.localGroup);
          this.closeDialog();
        } catch (err) {
          console.error(err);
        } finally {
          this.inProgress = false;
        }
      } else {
        this.inProgress = false;
        setTimeout(() => {
          this.$refs.form?.resetValidation();
        }, 4000);
      }
    },
  },
};
</script>

<style scoped>
.custom-border-radius {
  border-radius: 4px !important;
}
</style>
